import React, { useEffect, useState } from "react";
import {
  Users, Package, FileText, RefreshCw, Boxes, TrendingUp, CheckCircle2,
  XCircle, Clock, Plus, Settings, Save, Image as ImageIcon, AlertTriangle, Activity, Search,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError, API } from "@/lib/api";
import { formatINR } from "@/lib/format";
import ProductImg from "@/components/ProductImg";
import SyncBadge from "@/components/SyncBadge";
import AdminProductImages from "@/components/AdminProductImages";
import SyncHealthPanel from "@/components/SyncHealthPanel";
import StaffTab from "@/components/StaffTab";
import BuyerApprovals from "@/components/BuyerApprovals";
import WhatsAppLog from "@/components/WhatsAppLog";
import { useAuth } from "@/context/AuthContext";

const ORDER_STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900 font-mono-num">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [packReviewOnly, setPackReviewOnly] = useState(false);
  const [prodSearch, setProdSearch] = useState("");

  const loadProducts = (search = prodSearch) =>
    api.get("/admin/products", { params: {
      ...(packReviewOnly ? { pack_review: true } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    } })
      .then((r) => setProducts(r.data.products)).catch(() => {});

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
    loadProducts();
  };
  useEffect(loadAll, []);
  useEffect(() => { loadProducts(); }, [packReviewOnly]);
  useEffect(() => {
    const t = setTimeout(() => loadProducts(), 350);
    return () => clearTimeout(t);
  }, [prodSearch]);

  const applyPackRules = async () => {
    try {
      const { data } = await api.post("/admin/products/apply-pack-rules");
      toast.success(`Pack rules applied to ${data.updated} products · ${data.flagged_for_review} pcs items need manual review`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleVisibility = async (p, show) => {
    try {
      await api.patch(`/admin/products/${p.id}`, { admin_hidden: !show });
      setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, admin_hidden: !show } : x)));
      toast.success(show ? "Product shown on website" : "Product hidden from website");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateOrder = async (id, patch) => {
    try {
      await api.patch(`/admin/orders/${id}`, patch);
      toast.success(patch.payment_status === "paid" ? "Payment verified — order confirmed & sent to Zoho" : "Order updated");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      await api.post("/zoho/sync");
      toast.success("Zoho sync started — large catalogues take a couple of minutes.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSyncing(false);
      loadAll();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Portal</h1>
          <div className="hidden sm:block"><SyncBadge /></div>
          {isAdmin && (
            <Button onClick={runSync} disabled={syncing} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="zoho-sync-now-button">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sync Zoho now
            </Button>
          )}
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Clock} label="Pending buyers" value={stats.pending_buyers} accent="bg-amber-100 text-amber-700" />
            <StatCard icon={Users} label="Total buyers" value={stats.total_buyers} accent="bg-yellow-100 text-slate-800" />
            <StatCard icon={Package} label="Orders" value={stats.total_orders} accent="bg-indigo-100 text-indigo-700" />
            <StatCard icon={TrendingUp} label="Revenue" value={formatINR(stats.revenue)} accent="bg-emerald-100 text-emerald-700" />
          </div>
        )}

        <Tabs defaultValue="buyers">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="buyers" data-testid="admin-tab-buyers">Buyer Approvals</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products">Products &amp; Inventory</TabsTrigger>
            <TabsTrigger value="website" data-testid="admin-tab-website"><Settings className="h-4 w-4 mr-1.5" />Website Content</TabsTrigger>
            {isAdmin && <TabsTrigger value="staff" data-testid="admin-tab-staff"><Users className="h-4 w-4 mr-1.5" />Staff</TabsTrigger>}
            {isAdmin && <TabsTrigger value="synchealth" data-testid="admin-tab-synchealth"><Activity className="h-4 w-4 mr-1.5" />Sync Health</TabsTrigger>}
          </TabsList>

          {isAdmin && (
            <TabsContent value="synchealth" className="mt-6">
              <SyncHealthPanel />
              <WhatsAppLog />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="staff" className="mt-6">
              <StaffTab />
            </TabsContent>
          )}

          <TabsContent value="website" className="mt-6">
            <WebsiteContentTab />
          </TabsContent>

          {/* BUYERS */}
          <TabsContent value="buyers" className="mt-6">
            <BuyerApprovals onChange={loadAll} />
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6 space-y-3">
            {orders.length === 0 && <div className="text-center py-12 text-slate-400 bg-white rounded-2xl border">No orders yet.</div>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-mono-num font-semibold text-slate-900">{o.order_number}</div>
                    <div className="text-sm text-slate-500">{o.buyer_company} · {o.buyer_email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-num font-bold">{formatINR(o.total)}</span>
                    <Badge className="bg-slate-100 text-slate-700 border-0 capitalize" data-testid={`order-paystatus-${o.order_number}`}>{(o.payment_status || "").replaceAll("_", " ")}</Badge>
                    {["confirmed", "packed", "shipped", "delivered"].includes(o.status) && !o.zoho_salesorder_id && (
                      <Button size="sm" variant="outline" className="rounded-full text-rose-600 border-rose-200" data-testid={`retry-zoho-${o.order_number}`}
                        onClick={async () => { try { await api.post(`/admin/orders/${o.id}/push-zoho`); toast.success("Sales Order pushed to Zoho"); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
                        Retry Zoho push
                      </Button>
                    )}
                    {o.payment_method === "neft" && o.payment_status === "pending_verification" && (
                      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateOrder(o.id, { payment_status: "paid" })} data-testid={`verify-neft-${o.order_number}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid
                      </Button>
                    )}
                    <Select value={o.status} onValueChange={(v) => updateOrder(o.id, { status: v })}>
                      <SelectTrigger className="w-36 h-9" data-testid={`order-status-${o.order_number}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500">
                  {o.items.map((i) => `${i.name} ×${i.quantity}`).join("  ·  ")}
                </div>
                {(o.utr || o.transporter_name) && (
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                    {o.utr && <span data-testid={`order-utr-${o.order_number}`}>NEFT/RTGS UTR: <span className="font-mono-num font-semibold text-slate-800">{o.utr}</span></span>}
                    {o.transporter_name && <span>Transporter: <span className="font-semibold text-slate-800">{o.transporter_name}</span> · Hub: <span className="font-semibold text-slate-800">{o.hub_branch}</span></span>}
                  </div>
                )}
              </div>
            ))}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <div className="text-sm text-slate-500">
                {stats?.zoho_configured
                  ? "Live Zoho Inventory. Use Sync to refresh stock & prices."
                  : "Sample catalogue. Connect Zoho to sync live inventory, or add products manually."}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    value={prodSearch}
                    onChange={(e) => setProdSearch(e.target.value)}
                    placeholder="Search name, SKU or brand..."
                    className="pl-9 h-9 w-64 rounded-full bg-white"
                    data-testid="admin-product-search"
                  />
                </div>
                <Button
                  variant={packReviewOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPackReviewOnly((v) => !v)}
                  className={`rounded-full ${packReviewOnly ? "bg-amber-500 hover:bg-amber-600 text-white border-0" : "text-amber-700 border-amber-200"}`}
                  data-testid="pack-review-filter-btn"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Needs pack review ({stats?.pack_review ?? 0})
                </Button>
                <Button variant="outline" size="sm" onClick={applyPackRules} className="rounded-full" data-testid="apply-pack-rules-btn">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-apply pack rules
                </Button>
                <NewProductDialog onDone={loadAll} />
              </div>
            </div>
            {packReviewOnly && products.length === 0 && (
              <div className="text-center py-12 text-slate-400 bg-white rounded-2xl border" data-testid="pack-review-empty">No products need pack review.</div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-${p.sku || p.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
                  <div className="flex gap-3">
                    <ProductImg src={p.image_url} alt={p.name} className="h-16 w-16 rounded-lg object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-mono-num text-slate-400">{p.sku}</div>
                      <div className="font-medium text-slate-900 text-sm truncate">{p.name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-mono-num font-semibold">{formatINR(p.rate)}</span>
                        <Badge className={`border-0 ${p.stock_on_hand > 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{p.stock_on_hand} stk</Badge>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">MOQ {p.moq || 1} · pack {p.pack_size || 1} · {p.unit || "qty"}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                    <span className="text-xs font-medium text-slate-600">
                      {p.admin_hidden ? "Hidden from website" : "Shown on website"}
                    </span>
                    <Switch
                      checked={!p.admin_hidden}
                      onCheckedChange={(v) => toggleVisibility(p, v)}
                      data-testid={`visibility-toggle-${p.sku || p.id}`}
                    />
                  </div>
                  <div className="mt-2">
                    <AdminProductImages product={p} onDone={loadAll} />
                  </div>
                  {p.needs_pack_review && (
                    <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 p-2.5" data-testid={`pack-review-badge-${p.sku || p.id}`}>
                      <div className="flex items-center gap-1.5 text-xs text-amber-700 font-medium mb-2">
                        <AlertTriangle className="h-3.5 w-3.5" /> pcs item with no "Pack of X" in name — set rules manually
                      </div>
                      <PackRuleEditor product={p} onDone={loadAll} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PackRuleEditor({ product, onDone }) {
  const [moq, setMoq] = useState(product.moq || 1);
  const [pack, setPack] = useState(product.pack_size || 1);
  const save = async () => {
    try {
      await api.patch(`/admin/products/${product.id}`, {
        moq: Number(moq) || 1, pack_size: Number(pack) || 1, needs_pack_review: false,
      });
      toast.success("Pack rules saved");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min="1" value={moq} onChange={(e) => setMoq(e.target.value)} className="h-8 w-20 text-xs" title="MOQ" data-testid={`pack-moq-input-${product.sku}`} />
      <Input type="number" min="1" value={pack} onChange={(e) => setPack(e.target.value)} className="h-8 w-20 text-xs" title="Pack size (step)" data-testid={`pack-size-input-${product.sku}`} />
      <Button size="sm" className="h-8 rounded-full bg-slate-900 hover:bg-black text-white text-xs" onClick={save} data-testid={`pack-save-${product.sku}`}>Save</Button>
    </div>
  );
}

function WebsiteContentTab() {
  const { site, refreshSite } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/site").then((r) => setF(r.data)).catch(() => {});
  }, []);

  if (!f) return <div className="text-slate-400 py-12 text-center">Loading…</div>;

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setFeat = (i, k) => (e) =>
    setF((s) => ({ ...s, features: s.features.map((ft, idx) => (idx === i ? { ...ft, [k]: e.target.value } : ft)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/site", f);
      await refreshSite();
      toast.success("Website content updated — changes are live.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, k, area }) => (
    <div>
      <Label>{label}</Label>
      {area ? (
        <Textarea value={f[k] || ""} onChange={set(k)} rows={3} className="mt-1.5" data-testid={`site-${k}`} />
      ) : (
        <Input value={f[k] || ""} onChange={set(k)} className="mt-1.5" data-testid={`site-${k}`} />
      )}
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Edit the public homepage & footer. Changes save instantly — no code or credits needed.</p>
        <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-black text-white" data-testid="site-save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4">
        <div>
          <div className="font-heading font-semibold text-slate-900">Show out-of-stock products</div>
          <p className="text-xs text-slate-400 mt-0.5">Off (default) hides sold-out items so the catalogue stays clean. Turn on to show them with an "Out of stock" badge.</p>
        </div>
        <Switch checked={!!f.show_out_of_stock} onCheckedChange={(v) => setF((s) => ({ ...s, show_out_of_stock: v }))} data-testid="site-show-out-of-stock" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="font-heading font-semibold text-slate-900">Hero section</div>
        <Field label="Top badge" k="hero_badge" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Headline" k="hero_title" />
          <Field label="Headline highlight (yellow)" k="hero_title_accent" />
        </div>
        <Field label="Sub-text" k="hero_subtitle" area />
        <div className="grid sm:grid-cols-3 gap-4">
          {(f.features || []).map((ft, i) => (
            <div key={i} className="rounded-xl bg-slate-50 p-3 space-y-2">
              <Label className="text-xs">Card {i + 1} title</Label>
              <Input value={ft.title} onChange={setFeat(i, "title")} className="h-9" data-testid={`site-feature-${i}-title`} />
              <Label className="text-xs">Card {i + 1} description</Label>
              <Input value={ft.desc} onChange={setFeat(i, "desc")} className="h-9" data-testid={`site-feature-${i}-desc`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="font-heading font-semibold text-slate-900">Footer & contact</div>
        <Field label="Footer tagline" k="footer_tagline" />
        <Field label="About text" k="footer_about" area />
        <Field label="Address" k="address" area />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone(s)  (separate with · )" k="phone" />
          <Field label="Email" k="email" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business timing" k="timing" />
          <Field label="GSTIN" k="gstin" />
        </div>
      </div>
    </div>
  );
}

function NewProductDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", sku: "", category: "Food & Beverage", description: "", image_url: "", rate: "", mrp: "", stock_on_hand: "", moq: "1", pack_size: "1", unit: "qty" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    try {
      await api.post("/admin/products", {
        ...f, rate: Number(f.rate), mrp: Number(f.mrp || 0), stock_on_hand: Number(f.stock_on_hand || 0),
        moq: Number(f.moq || 1), pack_size: Number(f.pack_size || 1), tier_prices: [],
      });
      toast.success("Product added");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-slate-900 hover:bg-black text-white" data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1" /> Add product</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add product</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={set("name")} className="mt-1" data-testid="np-name" /></div>
          <div><Label>SKU</Label><Input value={f.sku} onChange={set("sku")} className="mt-1" data-testid="np-sku" /></div>
          <div><Label>Category</Label><Input value={f.category} onChange={set("category")} className="mt-1" data-testid="np-category" /></div>
          <div className="col-span-2"><Label>Image URL</Label><Input value={f.image_url} onChange={set("image_url")} className="mt-1" data-testid="np-image" /></div>
          <div className="col-span-2"><Label>Description</Label><Textarea value={f.description} onChange={set("description")} className="mt-1" rows={2} /></div>
          <div><Label>Rate ₹</Label><Input type="number" value={f.rate} onChange={set("rate")} className="mt-1" data-testid="np-rate" /></div>
          <div><Label>MRP ₹</Label><Input type="number" value={f.mrp} onChange={set("mrp")} className="mt-1" /></div>
          <div><Label>Stock</Label><Input type="number" value={f.stock_on_hand} onChange={set("stock_on_hand")} className="mt-1" data-testid="np-stock" /></div>
          <div><Label>Unit</Label><Input value={f.unit} onChange={set("unit")} className="mt-1" /></div>
          <div><Label>MOQ</Label><Input type="number" value={f.moq} onChange={set("moq")} className="mt-1" data-testid="np-moq" /></div>
          <div><Label>Pack size</Label><Input type="number" value={f.pack_size} onChange={set("pack_size")} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="w-full bg-slate-900 hover:bg-black text-white rounded-full" data-testid="save-product-btn">Save product</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
